import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FileUpload } from '@components/FileUpload';
import { datadogRum } from '@datadog/browser-rum';
import {
  ApplicationDocumentType,
  ApplicationEntityType,
  useVerifyIncomeDocumentUploadMutation,
} from '@graphql/generated/graphql';
import { useApplicationData } from '@provider';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { InformationCircleIcon } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { INCOME_VERIFICATION, getApplicantTypeAndTrackingIdFromUrl } from '@utils';
import GreenCheckIcon from '../../../assets/green-check.svg?react';
import { IncomeVerificationErrorPanel } from './IncomeVerificationErrorPanel';
import { INCOME_VERIFICATION_ERROR_CONTENT, IncomeVerificationErrorCode } from './IncomeVerificationErrors';
import { IncomeVerificationState } from './IncomeVerificationOptions';

type UploadDocumentProps = {
  name: string;
  setCurrentState: (state: IncomeVerificationState) => void;
  closeDialog: () => void;
  isBankError: boolean;
  isBankStatementEnabled?: boolean;
  incomeType?: 'primary' | 'additional';
  onDialogBusyChange?: (busy: boolean) => void;
  onVerificationStatusChange?: (status: VerificationStatus) => void;
};

type VerificationDocument = { documentId: string; fileName: string };

type VerificationResult = {
  documents: Array<{ documentId: string; validation: string; reason?: string | null; reasonCode?: string | null }>;
};

export type VerificationStatus = 'idle' | 'verifying' | 'success' | 'failed';

type VerificationStateModel = {
  status: VerificationStatus;
  stepIndex: number;
  errorCode: IncomeVerificationErrorCode | null;
  failureCount: number;
  documents: VerificationDocument[];
  result: VerificationResult | null;
};

type VerificationAction =
  | { type: 'start'; documents: VerificationDocument[] }
  | { type: 'step'; index: number }
  | { type: 'success'; result: VerificationResult }
  | { type: 'failed'; errorCode: IncomeVerificationErrorCode; result?: VerificationResult }
  | { type: 'reset-to-idle' };

const verificationStepIntervalsMs = [10000, 20000];
const verificationStepDurationMs = verificationStepIntervalsMs[verificationStepIntervalsMs.length - 1];

const initialVerificationState: VerificationStateModel = {
  status: 'idle',
  stepIndex: 0,
  errorCode: null,
  failureCount: 0,
  documents: [],
  result: null,
};

function verificationReducer(state: VerificationStateModel, action: VerificationAction): VerificationStateModel {
  switch (action.type) {
    case 'start':
      return {
        ...state,
        status: 'verifying',
        stepIndex: 0,
        errorCode: null,
        documents: action.documents,
        result: null,
      };
    case 'step':
      return { ...state, stepIndex: action.index };
    case 'success':
      return {
        ...state,
        status: 'success',
        result: action.result,
        failureCount: 0,
      };
    case 'failed':
      return {
        ...state,
        status: 'failed',
        errorCode: action.errorCode,
        result: action.result ?? state.result,
        failureCount: state.failureCount + 1,
      };
    case 'reset-to-idle':
      return { ...initialVerificationState, failureCount: state.failureCount };
    default:
      return state;
  }
}

function UploadDocuments({
  name,
  setCurrentState,
  closeDialog,
  isBankError,
  isBankStatementEnabled = true,
  incomeType,
  onDialogBusyChange,
  onVerificationStatusChange,
}: UploadDocumentProps) {
  const [verificationState, dispatchVerification] = useReducer(verificationReducer, initialVerificationState);
  const lastSuccessfulDocumentIdsRef = useRef<string[]>([]);
  const inFlightVerificationKeyRef = useRef<string | null>(null);
  const closeDialogRef = useRef(closeDialog);
  closeDialogRef.current = closeDialog;
  const existingDocumentsRef = useRef<VerificationDocument[]>([]);
  const {
    BANK_STATEMENT_CONNECT,
    UPLOAD,
    BANKING_STATEMENT_ERROR,
    UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION,
    UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION,
  } = INCOME_VERIFICATION;
  const { control, setValue } = useFormContext();
  const applicationId = useApplicationData('applicationId');
  const existingUploadedDocuments = useWatch({ control, name }) ?? [];
  existingDocumentsRef.current = (existingUploadedDocuments ?? []) as VerificationDocument[];
  const { applicantType } = getApplicantTypeAndTrackingIdFromUrl();
  const { mutateAsync: verifyIncomeDocumentUpload } = useVerifyIncomeDocumentUploadMutation({
    useErrorBoundary: true,
  });
  const flags = useFlags();
  const isIncomeVerificationFeedbackEnabled = flags['income-verification-feedback'] ?? false;
  const isPrimaryIncome = incomeType === 'primary';
  const shouldUseVerificationFeedback = isIncomeVerificationFeedbackEnabled && isPrimaryIncome;

  const verificationSteps = useMemo(
    () => [
      { label: 'Retrieving documents...', step: '1/3' },
      { label: 'Extracting information...', step: '2/3' },
      { label: 'Finishing up...', step: '3/3' },
    ],
    [],
  );
  const finalVerificationStepIndex = verificationSteps.length - 1;

  const maxVerificationFailures = 2;
  const hasReachedRetryLimit =
    shouldUseVerificationFeedback && verificationState.failureCount >= maxVerificationFailures;
  const shouldShowVerificationFailure = shouldUseVerificationFeedback && verificationState.status === 'failed';
  const incomeVerificationSubmitLabel =
    shouldUseVerificationFeedback && shouldShowVerificationFailure ? 'Update documents' : 'Verify documents';

  const documentIdsKey = useMemo(
    () =>
      verificationState.documents
        .map(document => document.documentId)
        .sort((left, right) => left.localeCompare(right))
        .join('|'),
    [verificationState.documents],
  );

  useEffect(() => {
    if (!shouldUseVerificationFeedback || verificationState.status !== 'verifying') {
      return;
    }

    const stepTimeouts = verificationStepIntervalsMs.map((delayMs, index) =>
      setTimeout(() => {
        dispatchVerification({ type: 'step', index: index + 1 });
      }, delayMs),
    );

    return () => {
      stepTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, [shouldUseVerificationFeedback, verificationState.status]);

  useEffect(() => {
    if (
      !shouldUseVerificationFeedback ||
      verificationState.status !== 'verifying' ||
      verificationState.documents.length === 0
    ) {
      return;
    }

    if (inFlightVerificationKeyRef.current === documentIdsKey) {
      return;
    }

    const currentDocumentIds = verificationState.documents
      .map(document => document.documentId)
      .sort((left, right) => left.localeCompare(right));
    const previousDocumentIds = [...lastSuccessfulDocumentIdsRef.current].sort((left, right) =>
      left.localeCompare(right),
    );
    const isSameDocumentSet =
      currentDocumentIds.length === previousDocumentIds.length &&
      currentDocumentIds.every((documentId, index) => documentId === previousDocumentIds[index]);

    if (isSameDocumentSet) {
      dispatchVerification({ type: 'reset-to-idle' });
      return;
    }

    let isCancelled = false;

    const waitForStepProgress = async (startTimeMs: number) => {
      if (!isPrimaryIncome) {
        return;
      }

      const elapsedMs = Date.now() - startTimeMs;
      const remainingMs = Math.max(verificationStepDurationMs - elapsedMs, 0);

      if (remainingMs > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingMs));
      }
    };

    const ensureFinalStepVisible = async () => {
      if (!isPrimaryIncome) {
        return;
      }

      if (verificationState.stepIndex < finalVerificationStepIndex) {
        dispatchVerification({ type: 'step', index: finalVerificationStepIndex });
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    };

    const verifyDocuments = async () => {
      const startTimeMs = Date.now();
      inFlightVerificationKeyRef.current = documentIdsKey;

      try {
        const existingDocIds = existingDocumentsRef.current.map(document => document.documentId);
        const newDocIds = verificationState.documents.map(document => document.documentId);
        const allDocumentIds = Array.from(new Set([...existingDocIds, ...newDocIds]));

        const response = await verifyIncomeDocumentUpload({
          VerifyIncomeDocumentUploadInput: {
            applicationId: applicationId ?? '',
            documentIds: allDocumentIds,
          },
        });

        await waitForStepProgress(startTimeMs);

        if (isCancelled) {
          return;
        }

        await ensureFinalStepVisible();

        const result = response.verifyIncomeDocumentUpload;
        const verificationResult = {
          documents: result.documents.map(document => ({
            documentId: document.documentId,
            validation: document.validation,
            reason: document.reason,
            reasonCode: document.failureCode,
          })),
        };

        if (result.validation === 'Failed') {
          const newFailureCount = verificationState.failureCount + 1;
          if (newFailureCount === 1) {
            datadogRum.addAction('Income document verification failed first attempt', {
              statusCode: result.statusCode,
              failureCount: newFailureCount,
            });
          } else if (newFailureCount === 2) {
            datadogRum.addAction('Income document verification failed second attempt', {
              statusCode: result.statusCode,
              failureCount: newFailureCount,
            });
          }
          dispatchVerification({
            type: 'failed',
            errorCode: result.statusCode as IncomeVerificationErrorCode,
            result: verificationResult,
          });
          if (shouldUseVerificationFeedback) {
            setValue('incomeVerification', 'Failed');
          }
          return;
        }

        if (shouldUseVerificationFeedback) {
          setValue('incomeVerification', undefined);
        }

        dispatchVerification({ type: 'success', result: verificationResult });
      } catch {
        await waitForStepProgress(startTimeMs);

        if (isCancelled) {
          return;
        }

        await ensureFinalStepVisible();

        const newFailureCount = verificationState.failureCount + 1;
        if (newFailureCount === 1) {
          datadogRum.addAction('Income document verification failed first attempt', {
            statusCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
            failureCount: newFailureCount,
          });
        } else if (newFailureCount === 2) {
          datadogRum.addAction('Income document verification failed second attempt', {
            statusCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
            failureCount: newFailureCount,
          });
        }
        dispatchVerification({
          type: 'failed',
          errorCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
        });
        if (shouldUseVerificationFeedback) {
          setValue('incomeVerification', 'Failed');
        }
      }
    };

    verifyDocuments();

    return () => {
      isCancelled = true;
    };
  }, [
    applicationId,
    documentIdsKey,
    finalVerificationStepIndex,
    shouldUseVerificationFeedback,
    verificationState.status,
    verifyIncomeDocumentUpload,
  ]);

  useEffect(() => {
    if (verificationState.status !== 'verifying') {
      inFlightVerificationKeyRef.current = null;
    }
  }, [verificationState.status]);

  useEffect(() => {
    onDialogBusyChange?.(verificationState.status === 'verifying');
  }, [verificationState.status, onDialogBusyChange]);

  useEffect(() => {
    onVerificationStatusChange?.(verificationState.status);
  }, [verificationState.status, onVerificationStatusChange]);

  useEffect(() => {
    if (!shouldUseVerificationFeedback || verificationState.status !== 'success') {
      return;
    }

    lastSuccessfulDocumentIdsRef.current = verificationState.documents
      .map(document => document.documentId)
      .sort((left, right) => left.localeCompare(right));
    const existingDocs = existingDocumentsRef.current;
    const existingIds = new Set(existingDocs.map(document => document.documentId));
    const mergedDocuments = [
      ...existingDocs,
      ...verificationState.documents.filter(document => !existingIds.has(document.documentId)),
    ];
    setValue(name, mergedDocuments);

    const closeTimeout = setTimeout(() => {
      closeDialogRef.current();
    }, 5000);

    return () => clearTimeout(closeTimeout);
  }, [verificationState.status]);

  const shouldShowVerificationProgress =
    shouldUseVerificationFeedback &&
    (verificationState.status === 'verifying' || verificationState.status === 'success');

  if (shouldShowVerificationProgress) {
    let verificationContent: React.ReactNode;

    if (verificationState.status === 'success') {
      verificationContent = (
        <>
          <Box display="flex" alignItems="center" justifyContent="center">
            <GreenCheckIcon />
          </Box>
          <Stack gap="medium" align="center">
            <Text weight="semibold" size="large">
              Your income has been verified!
            </Text>
            <Text align="center" tone="muted" size="small">
              We&apos;ve successfully verified your income from the documents you uploaded. Your application will now
              continue.
            </Text>
          </Stack>
          <Button disabled>Submitting documents</Button>
        </>
      );
    } else {
      verificationContent = (
        <>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Spinner tone="positive" />
          </Box>
          <Stack gap="xxlarge" align="center">
            <Text weight="semibold" size="large" align="center">
              {verificationSteps[verificationState.stepIndex]?.label} (
              {verificationSteps[verificationState.stepIndex]?.step})
            </Text>
            <Text align="center" tone="muted" size="small">
              Document verification typically takes 1-3 minutes.
              <br />
              Do not close this window.
            </Text>
          </Stack>
        </>
      );
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="center" paddingY="xxlarge" css={{ minHeight: '320px' }}>
        <Stack gap="xxlarge" align="center">
          {verificationContent}
        </Stack>
      </Box>
    );
  }

  const resolvedErrorCode =
    verificationState.errorCode && INCOME_VERIFICATION_ERROR_CONTENT[verificationState.errorCode]
      ? verificationState.errorCode
      : IncomeVerificationErrorCode.DOCUMENT_UNREADABLE;
  const resolvedErrorContent = INCOME_VERIFICATION_ERROR_CONTENT[resolvedErrorCode];
  const isMultipleDocumentFailure = resolvedErrorCode === IncomeVerificationErrorCode.MULTIPLE_DOCUMENT_TYPE_FAILED;
  const failedReasonCodes =
    verificationState.result?.documents
      .filter(document => document.validation !== 'Passed')
      .map(document => document.reasonCode ?? document.reason)
      .filter((reason): reason is IncomeVerificationErrorCode =>
        Boolean(reason && reason in INCOME_VERIFICATION_ERROR_CONTENT),
      ) ?? [];
  const failedReasonContents = Array.from(
    new Set(failedReasonCodes.map(reason => INCOME_VERIFICATION_ERROR_CONTENT[reason])),
  );
  const multipleDescriptions = Array.from(
    new Set(
      failedReasonContents
        .map(content => content.description)
        .filter((description): description is string => Boolean(description)),
    ),
  );
  const errorPanelProps = isMultipleDocumentFailure
    ? {
        title: resolvedErrorContent.title,
        descriptionItems: multipleDescriptions,
      }
    : {
        title: resolvedErrorContent.title,
        description: resolvedErrorContent.description,
        callout: resolvedErrorContent.callout,
        guidanceTitle: resolvedErrorContent.guidanceTitle,
        guidanceItems: resolvedErrorContent.guidanceItems,
      };
  const uploadedDocuments = verificationState.documents.map(document => {
    const validation = verificationState.result?.documents.find(result => result.documentId === document.documentId);
    return {
      name: document.fileName,
      status: validation?.validation === 'Passed' ? ('passed' as const) : ('failed' as const),
    };
  });

  const legacyContent = (
    <Stack gap="xxlarge">
      {isBankError && (
        <Alert tone="caution" icon={InformationCircleIcon}>
          <Text tone="muted">{BANKING_STATEMENT_ERROR}</Text>
        </Alert>
      )}
      <>
        {incomeType === 'primary' ? (
          <Text>{UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION()}</Text>
        ) : (
          <Text>{UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION()}</Text>
        )}

        <FileUpload
          title={UPLOAD}
          fieldName={name}
          setFieldValue={setValue}
          applicationId={applicationId ?? ''}
          existingUploadedDocuments={existingUploadedDocuments}
          documentType={ApplicationDocumentType.IncomeVerification}
          applicantType={ApplicationEntityType[applicantType]}
          submitLabel={incomeVerificationSubmitLabel}
          onVerificationStart={uploadedDocuments => {
            if (shouldUseVerificationFeedback) {
              dispatchVerification({ type: 'start', documents: uploadedDocuments });
              return;
            }

            if (!isPrimaryIncome) {
              closeDialog();
              return;
            }

            const existingDocIds = existingDocumentsRef.current.map(document => document.documentId);
            const newDocIds = uploadedDocuments.map(document => document.documentId);
            const allDocumentIds = Array.from(new Set([...existingDocIds, ...newDocIds]));
            verifyIncomeDocumentUpload({
              VerifyIncomeDocumentUploadInput: {
                applicationId: applicationId ?? '',
                documentIds: allDocumentIds,
              },
            });

            closeDialog();
          }}
        />
        {isBankStatementEnabled ? (
          <Box>
            <Button
              prominence="low"
              tone="neutral"
              onClick={() => setCurrentState(IncomeVerificationState.BANK_STATEMENT_VERIFICATION)}
            >
              {BANK_STATEMENT_CONNECT}
            </Button>
          </Box>
        ) : null}
      </>
    </Stack>
  );

  return shouldUseVerificationFeedback ? (
    <Stack gap="xxlarge" align="stretch">
      {shouldShowVerificationFailure ? (
        <Box padding="large" borderRadius="medium" border="caution">
          <Stack gap="large" align="stretch">
            <IncomeVerificationErrorPanel {...errorPanelProps} uploadedDocuments={uploadedDocuments} />
            <Divider />
            <Accordion type="single" collapsible>
              <AccordionItem label="What documents can I upload?" value="what-documents">
                {incomeType === 'primary'
                  ? UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION()
                  : UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION()}
              </AccordionItem>
            </Accordion>
            <FileUpload
              title={UPLOAD}
              fieldName={name}
              setFieldValue={setValue}
              applicationId={applicationId ?? ''}
              existingUploadedDocuments={existingUploadedDocuments}
              documentType={ApplicationDocumentType.IncomeVerification}
              applicantType={ApplicationEntityType[applicantType]}
              onVerificationStart={uploadedDocuments => {
                dispatchVerification({ type: 'start', documents: uploadedDocuments });
              }}
              onVerificationError={() => dispatchVerification({ type: 'reset-to-idle' })}
              isVerificationFailed={shouldShowVerificationFailure}
              isUploadDisabled={hasReachedRetryLimit}
              submitLabel={
                hasReachedRetryLimit && shouldUseVerificationFeedback
                  ? 'Continue and verify manually'
                  : incomeVerificationSubmitLabel
              }
              allowSubmitWhenNoSelection={hasReachedRetryLimit && shouldUseVerificationFeedback}
              onManualContinue={
                hasReachedRetryLimit && shouldUseVerificationFeedback
                  ? () => {
                      closeDialog();
                    }
                  : undefined
              }
            />
          </Stack>
        </Box>
      ) : null}
      {isBankError && (
        <Alert tone="caution" icon={InformationCircleIcon}>
          <Text tone="muted">{BANKING_STATEMENT_ERROR}</Text>
        </Alert>
      )}
      {!shouldShowVerificationFailure ? (
        <>
          <Box>
            {incomeType === 'primary' ? (
              <Text>{UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION()}</Text>
            ) : (
              <Text>{UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION()}</Text>
            )}
          </Box>
          <FileUpload
            title={UPLOAD}
            fieldName={name}
            setFieldValue={setValue}
            applicationId={applicationId ?? ''}
            existingUploadedDocuments={existingUploadedDocuments}
            documentType={ApplicationDocumentType.IncomeVerification}
            applicantType={ApplicationEntityType[applicantType]}
            onVerificationStart={uploadedDocuments => {
              dispatchVerification({ type: 'start', documents: uploadedDocuments });
            }}
            onVerificationError={() => dispatchVerification({ type: 'reset-to-idle' })}
            submitLabel={incomeVerificationSubmitLabel}
          />
          {isBankStatementEnabled ? (
            <Box>
              <Button
                prominence="low"
                tone="neutral"
                onClick={() => setCurrentState(IncomeVerificationState.BANK_STATEMENT_VERIFICATION)}
              >
                {BANK_STATEMENT_CONNECT}
              </Button>
            </Box>
          ) : null}
        </>
      ) : null}
    </Stack>
  ) : (
    legacyContent
  );
}

export default UploadDocuments;
